import { useEffect, useState, useRef } from "react";
import { api, formatDate, API_BASE } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Upload, FileIcon, Trash2, Download, Folder } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "PAN", "Aadhaar", "Passport", "Driving Licence", "Voter ID",
  "Will", "Marriage Certificate", "Birth Certificate",
  "Property Papers", "Insurance Policy", "Loan Document",
  "Tax Return", "Investment Statement", "Mediclaim Card", "Other",
];

export default function Vault() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ category: "", holder_name: "", expiry_date: "", tags: "" });
  const [filter, setFilter] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/files");
      setFiles(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    if (meta.category) fd.append("category", meta.category);
    if (meta.holder_name) fd.append("holder_name", meta.holder_name);
    if (meta.expiry_date) fd.append("expiry_date", meta.expiry_date);
    if (meta.tags) fd.append("tags", meta.tags);
    setUploading(true);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setMeta({ category: "", holder_name: "", expiry_date: "", tags: "" });
      await load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (f) => {
    try {
      const resp = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (f) => {
    if (!confirm(`Delete ${f.filename}?`)) return;
    await api.delete(`/files/${f.id}`);
    toast.success("Deleted");
    await load();
  };

  const filtered = filter
    ? files.filter((f) => f.category === filter)
    : files;
  const categories = Array.from(new Set(files.map((f) => f.category).filter(Boolean)));

  return (
    <div className="space-y-6" data-testid="vault-page">
      <div>
        <div className="label-overline">Document Vault</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
          Secure Document Vault
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Upload PDFs, scanned documents, and images. Files are stored in encrypted object
          storage; only you (and explicitly-authorised emergency contacts) can access them.
        </p>
      </div>

      <Card className="p-6" data-testid="vault-upload-card">
        <div className="font-heading font-semibold mb-4">Upload a new document</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={meta.category} onValueChange={(v) => setMeta({ ...meta, category: v })}>
              <SelectTrigger data-testid="vault-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Holder Name</Label>
            <Input value={meta.holder_name} onChange={(e) => setMeta({ ...meta, holder_name: e.target.value })}
              data-testid="vault-holder-input" />
          </div>
          <div>
            <Label>Expiry Date (optional)</Label>
            <Input type="date" value={meta.expiry_date} onChange={(e) => setMeta({ ...meta, expiry_date: e.target.value })}
              data-testid="vault-expiry-input" />
          </div>
          <div>
            <Label>Tags (comma separated)</Label>
            <Input value={meta.tags} onChange={(e) => setMeta({ ...meta, tags: e.target.value })}
              data-testid="vault-tags-input" />
          </div>
        </div>
        <div className="mt-6 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50/50">
          <input ref={inputRef} type="file" className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            data-testid="vault-file-input"
          />
          <Upload className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
          <div className="mt-3 font-medium text-slate-700">
            {uploading ? "Uploading..." : "Click to select a file to upload"}
          </div>
          <div className="text-xs text-slate-500 mt-1">PDF, image, document — up to several MB</div>
          <Button className="mt-4" onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid="vault-choose-file-button">
            <Upload className="h-4 w-4 mr-2" /> Choose file
          </Button>
        </div>
      </Card>

      <div className="flex gap-2 flex-wrap">
        <Button variant={!filter ? "default" : "outline"} size="sm" onClick={() => setFilter("")}
          data-testid="vault-filter-all">
          <Folder className="h-4 w-4 mr-2" /> All ({files.length})
        </Button>
        {categories.map((c) => (
          <Button key={c} variant={filter === c ? "default" : "outline"} size="sm"
            onClick={() => setFilter(c)} data-testid={`vault-filter-${c}`}>
            {c}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-500 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 border-dashed text-center">
          <FileIcon className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
          <div className="mt-3 font-medium">No documents yet</div>
          <div className="text-sm text-slate-500 mt-1">Upload your first document to the vault.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((f) => (
            <Card key={f.id} className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded bg-slate-100 flex items-center justify-center shrink-0">
                  <FileIcon className="h-5 w-5 text-slate-600" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate" title={f.filename}>
                    {f.filename}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {Math.round((f.size || 0) / 1024)} KB · {formatDate(f.created_at)}
                  </div>
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {f.category && <Badge variant="outline" className="text-[10px]">{f.category}</Badge>}
                    {f.holder_name && <Badge variant="secondary" className="text-[10px]">{f.holder_name}</Badge>}
                    {f.expiry_date && <Badge variant="outline" className="text-[10px]">Exp: {formatDate(f.expiry_date)}</Badge>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => download(f)} data-testid={`vault-download-${f.id}`}>
                  <Download className="h-4 w-4 mr-1" /> Download
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(f)} data-testid={`vault-delete-${f.id}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
